import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { OrganizationStructuredData, WebsiteStructuredData } from '../components/StructuredData';
import AdSense from '../components/AdSense';
import {
  TrendingUp,
  GraduationCap,
  Smartphone,
  Users,
  Shield,
  ArrowRight,
  Calculator,
  PiggyBank,
  BookOpen
} from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';

const Home: React.FC = () => {
  // SEO implementation
  useSEO({
    title: 'O Teu Dinheiro - Finanças Pessoais para o Brasileiro',
    description: 'Educação financeira simples e prática para o brasileiro. Dicas de economia, investimentos, PIX, cartão de crédito, dívidas e muito mais.',
    keywords: 'finanças pessoais, educação financeira, investimentos, economia, PIX, cartão de crédito, dívidas, Brasil, dinheiro, poupança, aposentadoria',
    type: 'website'
  });
  const features = [
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'Educação Financeira',
      description: 'Conteúdo explicado de forma simples e acessível para todos'
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: 'Atualizado',
      description: 'Informações sobre PIX e novas tecnologias financeiras'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Para Todos',
      description: 'Dicas para famílias de baixa e média renda'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Confiável',
      description: 'Informações baseadas em fontes seguras e confiáveis'
    }
  ];

  const quickActions = [
    {
      icon: <Calculator className="w-6 h-6" />,
      title: 'Gerenciar Dívidas',
      description: 'Estratégias para sair do vermelho',
      href: '/dividas',
      color: 'bg-red-500'
    },
    {
      icon: <PiggyBank className="w-6 h-6" />,
      title: 'Dicas de Economia',
      description: 'Como economizar no dia a dia',
      href: '/economia',
      color: 'bg-green-500'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Investimentos',
      description: 'Comece a investir com segurança',
      href: '/investimentos',
      color: 'bg-blue-500'
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Blog',
      description: 'Dicas semanais de finanças',
      href: '/blog',
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Structured Data */}
      <WebsiteStructuredData />
      <OrganizationStructuredData
        name="O Teu Dinheiro"
        description="Educação financeira simples e prática para o brasileiro"
        url="https://www.oteudinheiro.com.br"
        logo="https://www.oteudinheiro.com.br/logo512.png"
        email="contato@oteudinheiro.com.br"
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50 opacity-70"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-100 rounded-full blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-secondary-100 rounded-full blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '2s' }}></div>

        <div className="container-custom relative pt-20 pb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-10 animate-slide-up">
              <div className="inline-flex items-center bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-primary-100 ring-1 ring-primary-50">
                <span className="animate-pulse w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                <span className="text-sm font-semibold text-primary-800 tracking-wide uppercase">Dicas atualizadas para 2026</span>
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-[1.1] tracking-tight">
                Domine seu <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">Dinheiro</span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                Junte-se a milhares de brasileiros que estão saindo das dívidas e aprendendo a investir com nossos guias práticos e ferramentas gratuitas.
              </p>

              <div className="flex flex-col sm:flex-row gap-5">
                <Link to="/calculadoras" className="group relative inline-flex items-center justify-center px-8 py-4 font-semibold text-white transition-all duration-200 bg-primary-600 rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 shadow-lg shadow-primary-600/30 hover:shadow-primary-600/40 hover:-translate-y-1">
                  <Calculator className="w-5 h-5 mr-2" />
                  Usar Calculadoras
                  <div className="absolute inset-0 rounded-xl ring-2 ring-white/20 group-hover:ring-white/40 transition-all"></div>
                </Link>
                <Link to="/blog" className="inline-flex items-center justify-center px-8 py-4 font-semibold text-gray-700 transition-all duration-200 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-primary-600 hover:border-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 shadow-sm hover:shadow-md hover:-translate-y-1">
                  <BookOpen className="w-5 h-5 mr-2" />
                  Ler Artigos
                </Link>
              </div>

              <div className="pt-8 border-t border-gray-100 flex items-center gap-12">
                <div>
                  <p className="text-3xl font-bold text-gray-900">50+</p>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mt-1">Artigos Gratuitos</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">8</p>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mt-1">Calculadoras Online</p>
                </div>
              </div>
            </div>

            <div className="relative hidden lg:block perspective-1000">
              {/* Card Flutuante 1 - Simulando Investimento */}
              <div className="absolute top-0 left-0 bg-white/90 backdrop-blur-md p-5 rounded-2xl shadow-2xl z-20 animate-float-slow border border-white/50 ring-1 ring-black/5 w-64">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-3 rounded-xl">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Rendimento</p>
                    <p className="text-lg font-bold text-green-600">+ R$ 1.250,00</p>
                  </div>
                </div>
              </div>

              {/* Card Flutuante 2 - Simulando Economia */}
              <div className="absolute bottom-10 -right-4 bg-white/90 backdrop-blur-md p-5 rounded-2xl shadow-2xl z-20 animate-float-delayed border border-white/50 ring-1 ring-black/5 w-64">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 p-3 rounded-xl">
                    <PiggyBank className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Reserva</p>
                    <p className="text-lg font-bold text-blue-600">Meta: 100%</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-[2.5rem] p-12 text-white text-center shadow-2xl shadow-primary-900/20 mx-8 relative z-10 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 rounded-[2.5rem]"></div>
                <TrendingUp className="w-24 h-24 mx-auto mb-8 text-white/90 drop-shadow-lg" />
                <h3 className="text-3xl font-bold mb-6">Seu Guia Financeiro</h3>
                <p className="text-lg opacity-90 mb-10 leading-relaxed font-light">
                  Transforme sua vida financeira hoje mesmo com conhecimento prático e ferramentas poderosas.
                </p>
                <div className="inline-block bg-white/10 backdrop-blur-md rounded-xl px-8 py-4 border border-white/20 shadow-inner">
                  <p className="text-base font-medium">Comece sua jornada 👇</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white relative">
        <div className="container-custom">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Por que escolher o <span className="text-primary-600">O Teu Dinheiro</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Nossa missão é democratizar a educação financeira no Brasil,
              tornando-a acessível, simples e prática para todos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group p-8 rounded-3xl bg-gray-50 hover:bg-white border border-gray-100 hover:border-primary-100 hover:shadow-xl hover:shadow-primary-900/5 transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 group-hover:bg-primary-50 transition-all duration-300">
                  <div className="text-primary-600 group-hover:text-primary-700 transition-colors">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-700 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="py-24 bg-gray-50">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Comece Agora
              </h2>
              <p className="text-xl text-gray-600">
                Escolha o tema que mais te interessa e inicie sua transformação financeira.
              </p>
            </div>
            <Link to="/calculadoras" className="text-primary-600 font-semibold hover:text-primary-700 flex items-center gap-2 group">
              Ver todas as ferramentas <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.href}
                className="group relative overflow-hidden bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${action.color.replace('bg-', 'from-').replace('500', '50')} to-transparent opacity-50 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-150 duration-500`}></div>

                <div className={`w-14 h-14 ${action.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-${action.color}/20 group-hover:scale-110 transition-transform duration-300 relative z-10`}>
                  <div className="text-white">
                    {action.icon}
                  </div>
                </div>

                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-700 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {action.description}
                  </p>
                  <div className="flex items-center text-primary-600 font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300">
                    Acessar Agora <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Trending Guides & Simulators (Internal Link Hub for Top GSC Pages) */}
          <div className="mt-20">
            <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                  🔥 Em Alta em 2026
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mt-3">
                  Guias e Simuladores Mais Buscados
                </h2>
              </div>
              <Link to="/blog" className="text-primary-600 font-semibold hover:text-primary-700 flex items-center gap-2">
                Ver todos os artigos <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link
                to="/blog/etf-bitcoin-bolsa-vale-pena"
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 hover:border-primary-200 flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-semibold text-indigo-600 uppercase">Investimentos • B3</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                    ETF de Bitcoin na Bolsa (HASH11, BITH11, IBIT39): Vale a Pena em 2026?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Comparativo de taxas, custódia e Imposto de Renda entre ETFs na B3 e compra direta de cripto.
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary-600 inline-flex items-center">
                  Ler comparativo <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/melhores-cartoes-2026-alta-renda"
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 hover:border-primary-200 flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-semibold text-indigo-600 uppercase">Alta Renda • Salas VIP</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                    Melhores Cartões Black 2026: Ranking Atualizado (Milhas e Cashback)
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Comparamos BRB Dux, C6 Graphene, XP Legacy, Ultravioleta e Inter Black com quiz interativo.
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary-600 inline-flex items-center">
                  Ver ranking 2026 <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/nomad-wise-global-2026"
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 hover:border-primary-200 flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-semibold text-indigo-600 uppercase">Câmbio • Conta Global</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                    Nomad ou Wise em 2026? Calculadora de Dólar, Euro e IOF
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Simule sua economia real no exterior comparando Wise, Nomad e cartão de crédito tradicional.
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary-600 inline-flex items-center">
                  Simular câmbio <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/fim-isencao-imposto-renda-lci-lca-2026"
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 hover:border-primary-200 flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-semibold text-indigo-600 uppercase">Renda Fixa • Simulador</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                    Fim da Isenção de IR em LCI e LCA? Simulador LCI vs. CDB
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Calcule qual taxa de CDB equivale à sua LCI/LCA e veja o impacto real no seu bolso.
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary-600 inline-flex items-center">
                  Usar simulador <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/carro-assinatura-calculadora"
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 hover:border-primary-200 flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-semibold text-indigo-600 uppercase">Calculadora • Veículos</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                    Carro por Assinatura Vale a Pena em 2026? Calculadora Completa
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Colocamos IPVA, seguro, depreciação e CDI na ponta do lápis: assinar ou comprar à vista?
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary-600 inline-flex items-center">
                  Fazer as contas <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/cbs-ibs-guia-2026"
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 hover:border-primary-200 flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-semibold text-indigo-600 uppercase">Reforma Tributária</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                    CBS e IBS em 2026: Guia Prático do Novo IVA Dual e Cashback
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Veja a tabela do que fica mais barato, o que sobe e como funciona o Cashback de Impostos.
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary-600 inline-flex items-center">
                  Ler guia completo <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Section */}
      <section className="py-12 bg-white">
        <div className="container-custom px-4">
          <AdSense />
        </div>
      </section>

      {/* Newsletter Section */}
      <div className="bg-gray-50 pb-20">
        <div className="container-custom">
          <NewsletterSignup />
        </div>
      </div>
    </div>
  );
};

export default Home;
